import { VersionCheckerConfig } from "../types/update.types";
import { VersionFetcher, VersionMismatchError } from "./VersionFetcher.service";

/**
 * Service that periodically checks for version mismatches between client and server.
 */
export class VersionChecker {
  private intervalId: NodeJS.Timeout | null = null;
  private isChecking = false;
  private readonly config: VersionCheckerConfig;
  private onVersionMismatchHandler?: () => void;

  /**
   * Creates a new VersionChecker instance.
   * @param versionFetcher - Service to fetch and compare versions
   * @param config - Configuration options for version checking
   */
  constructor(
    private readonly versionFetcher: VersionFetcher,
    config: Partial<VersionCheckerConfig> = {}
  ) {
    this.config = {
      checkInterval: 0,
      checkImmediately: true,
      ...config,
    };
  }

  /**
   * Registers a callback to be executed when a version mismatch is detected.
   * @param handler - Callback function to execute on version mismatch
   * @returns The VersionChecker instance for method chaining
   */
  public onVersionMismatch(handler: () => void): VersionChecker {
    this.onVersionMismatchHandler = handler;

    return this;
  }

  /**
   * Starts periodic version checking based on the configured interval.
   * If checkImmediately is true, performs an initial check before starting the interval.
   * Does nothing if already running or if checkInterval is 0.
   */
  public async start(): Promise<void> {
    if (this.isRunning() || this.config.checkInterval === 0) {
      return;
    }

    if (this.config.checkImmediately) {
      await this.check();
    }

    this.intervalId = setInterval(
      () => this.check(),
      this.config.checkInterval
    );
  }

  /**
   * Stops periodic version checking and aborts any in-progress checks.
   */
  public stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      this.versionFetcher.abort();
      this.isChecking = false;
    }
  }

  /**
   * Checks if the version checker is currently running.
   * @returns True if periodic checking is active, false otherwise
   */
  public isRunning(): boolean {
    return this.intervalId !== null;
  }

  /**
   * Performs a single version check if not already in progress.
   * Executes the registered handler if a version mismatch is detected.
   * @private
   */
  private async check(): Promise<void> {
    if (this.isChecking) {
      return;
    }

    try {
      this.isChecking = true;
      await this.versionFetcher.checkVersion();
    } catch (error) {
      if (error instanceof VersionMismatchError) {
        this.onVersionMismatchHandler?.();
      }
    } finally {
      this.isChecking = false;
    }
  }
}
